/* ==============================================================================
   Lista de mensagens e composição
   ==============================================================================
   Três problemas que qualquer lista de chat tem, e como cada um é resolvido aqui:

   1. **Rolagem que salta ao carregar histórico.**
      Carregar mensagens antigas insere conteúdo ACIMA do que está visível, e o
      navegador mantém `scrollTop` — então a viewport "sobe" e o usuário perde o
      lugar. A correção é medir `scrollHeight` antes e depois e compensar a
      diferença. Sem isso, rolar para cima é inutilizável.

   2. **Rolagem automática que atropela quem está lendo.**
      Mensagem nova deve rolar para baixo SÓ se a pessoa já estava no fim. Rolar
      sempre arranca o usuário do trecho que ele estava lendo; nunca rolar faz a
      conversa ao vivo parecer travada. O limiar é "a menos de 120px do fim".

   3. **Agrupamento por autor.**
      Repetir avatar e nome em cada mensagem de uma sequência do mesmo autor
      desperdiça a altura e polui. Agrupa quando é o mesmo autor dentro de 5
      minutos — acima disso, o intervalo é informação e o cabeçalho volta.
   ============================================================================== */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Avatar, type AvatarMedia } from '../components/Avatar.tsx';

/* ------------------------------------------------------------------------------
   Modelo
------------------------------------------------------------------------------ */

export interface MessageAuthor {
  id: string;
  username: string;
  displayName: string | null;
  nickname: string | null;
  avatar?: AvatarMedia | null;
  /** Cor do cargo mais alto com cor. */
  color?: string | null;
  isBot?: boolean;
}

export interface MessageAttachment {
  id: string;
  filename: string;
  url: string;
  mimeType: string;
  byteSize: number;
  width?: number | null;
  height?: number | null;
  durationMs?: number | null;
  spoiler?: boolean;
}

export interface ChatMessage {
  /** Snowflake como string. */
  id: string;
  channelId: string;
  content: string | null;
  createdAt: string;
  editedAt: string | null;
  author: MessageAuthor | null;
  attachments?: MessageAttachment[];
  /** Enviada e ainda não confirmada pelo servidor. */
  pending?: boolean;
  /** Falhou ao enviar. Permite tentar de novo. */
  failed?: boolean;

  /* --------------------------------------------------------------------------
     Menções — resolvidas pelo SERVIDOR, não pelo cliente
     --------------------------------------------------------------------------
     São ids e não nomes de propósito. Decidir "fui mencionado?" a partir do
     texto exigiria casar `@` + nome, e nome muda: quem trocou o apelido
     deixaria de receber destaque nas mensagens antigas, e quem tem o apelido
     parecido com o de outra pessoa receberia destaque alheio. Id é estável.
  -------------------------------------------------------------------------- */
  /** UUIDs mencionados diretamente. */
  mentionUsers?: string[] | undefined;
  /** Ids de cargos mencionados. */
  mentionRoles?: string[] | undefined;
  mentionEveryone?: boolean | undefined;
  /** Fixada no canal. */
  pinned?: boolean | undefined;
}

/** Item da faixa de fixadas. */
export interface PinnedMessage {
  messageId: string;
  content: string | null;
  authorName: string;
}

export interface MessageListProps {
  messages: ChatMessage[];
  currentUserId: string;
  /**
   * Cargos do usuário atual, para resolver menção de cargo.
   *
   * Sem isto, `@moderadores` nunca destacaria nada — o cliente saberia que a
   * mensagem menciona um cargo e não saberia se é um dos meus.
   */
  currentUserRoleIds?: string[] | undefined;

  /** Mensagens fixadas do canal. Vazio ou ausente = sem faixa. */
  pins?: PinnedMessage[] | undefined;
  /**
   * Fixar e desfixar.
   *
   * AUSENTE significa "sem permissão", e a ação simplesmente não aparece — não
   * aparece desabilitada. Um item cinza num menu de mensagem ensina a ignorar o
   * menu, e fixar é permissão de cargo: a maioria dos membros nunca a terá.
   */
  onTogglePin?: ((messageId: string, pinned: boolean) => void) | undefined;
  /** Ir até uma mensagem — usado pela faixa de fixadas. */
  onJumpToMessage?: ((messageId: string) => void) | undefined;
  /** Nome do canal, para o cabeçalho de "início da conversa". */
  channelName: string;
  channelTopic?: string | null;
  /**
   * Conversa entre duas pessoas em vez de canal de servidor.
   *
   * Muda três coisas, e todas elas porque um canal e uma conversa não são a
   * mesma coisa apesar de usarem o mesmo transporte:
   *
   *   - o marco de início perde o `#`, que é a sintaxe de CANAL. `#Concord` para
   *     uma conversa com Concord lê como um canal de nome igual ao da pessoa,
   *     e foi o relato.
   *   - as mensagens viram BOLHAS alinhadas por autor. Numa conversa de dois, de
   *     quem é a mensagem é a única desambiguação necessária, e o lado resolve
   *     isso antes de o olho chegar ao nome.
   *   - o rótulo do início fala de conversa e não de canal.
   *
   * Num canal de servidor as bolhas seriam erradas: com dez pessoas falando, o
   * lado não identifica ninguém, e alinhar tudo à esquerda com avatar é o que
   * permite varrer a coluna de autores.
   */
  direct?: boolean;
  /** `true` enquanto uma página de histórico carrega. */
  loadingOlder?: boolean;
  /** `false` quando o início da conversa já foi alcançado. */
  hasMore?: boolean;
  onLoadOlder?: () => void;
  onRetry?: (messageId: string) => void;
  onAuthorClick?: (userId: string) => void;
  /** Quem está digitando agora, sem incluir o próprio usuário. */
  typing?: string[];
  emptyHint?: ReactNode;
}

/** Agrupa quando é o mesmo autor em até 5 minutos. */
const GROUP_WINDOW_MS = 5 * 60 * 1000;
/** Distância do fim que ainda conta como "está no fim". */
const AT_BOTTOM_THRESHOLD = 120;

/**
 * Reconhece `@algo` dentro do texto.
 *
 * `[\p{L}\p{N}_.\-]` e não `\w`: apelido com acento é a regra e não a exceção em
 * português, e `\w` pararia em "João" no "o", produzindo uma cápsula "@Jo" e um
 * "ão" solto do lado de fora.
 *
 * A captura fica no grupo para `split` devolver os separadores intercalados —
 * é o que permite reconstruir o texto sem perder nada.
 */
const MENTION_SPLIT = /(@[\p{L}\p{N}_.\-]+)/gu;

/**
 * Fui mencionado nesta mensagem?
 *
 * A ordem dos testes é do mais específico para o mais amplo, e o mais amplo
 * (`@everyone`) vem por último porque é o que menos significa: numa mensagem
 * dirigida a mim E ao canal, o destaque é por mim.
 */
function mentionsMe(
  message: ChatMessage,
  userId: string,
  roleIds: readonly string[],
): boolean {
  if (message.mentionUsers?.includes(userId) === true) return true;
  if (
    message.mentionRoles !== undefined &&
    roleIds.length > 0 &&
    message.mentionRoles.some((id) => roleIds.includes(id))
  ) {
    return true;
  }
  return message.mentionEveryone === true;
}

/**
 * Texto da mensagem com as menções em cápsula.
 *
 * Por PARTIÇÃO DE STRING e nunca `dangerouslySetInnerHTML`.
 *
 * O conteúdo é texto digitado por um usuário qualquer da instância. Injetá-lo
 * como HTML para "só" pintar as menções entregaria XSS armazenado a qualquer
 * pessoa capaz de mandar mensagem — e o payload ficaria no banco, servido a
 * todos que abrissem o canal depois. Não existe sanitização que valha o risco
 * quando a alternativa é `split()` e um `<span>`.
 */
function renderContent(content: string, highlighted: boolean): ReactNode[] {
  return content.split(MENTION_SPLIT).map((part, index) => {
    if (!part.startsWith('@') || part.length < 2) {
      // Chave por índice é correta aqui: as partes são posicionais e a lista
      // inteira é recriada quando o texto muda. Não há reordenação possível.
      return <span key={index}>{part}</span>;
    }
    return (
      <span
        key={index}
        className="font-medium"
        style={{
          padding: '1px 4px',
          margin: '0 -1px',
          borderRadius: 'var(--radius-sm)',
          background: highlighted
            ? 'var(--color-accent-solid)'
            : 'var(--color-accent-subtle)',
          color: highlighted
            ? 'var(--color-on-solid)'
            : 'var(--color-accent-on-subtle)',
        }}
      >
        {part}
      </span>
    );
  });
}

function displayNameOf(author: MessageAuthor): string {
  return author.nickname ?? author.displayName ?? author.username;
}

function formatTime(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function formatDayDivider(iso: string): string {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86_400_000);

  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (sameDay(date, today)) return 'Hoje';
  if (sameDay(date, yesterday)) return 'Ontem';
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: date.getFullYear() === today.getFullYear() ? undefined : 'numeric',
  });
}

export function MessageList({
  messages,
  currentUserId,
  currentUserRoleIds = [],
  pins,
  onTogglePin,
  onJumpToMessage,
  channelName,
  channelTopic,
  direct = false,
  loadingOlder = false,
  hasMore = true,
  onLoadOlder,
  onRetry,
  onAuthorClick,
  typing = [],
  emptyHint,
}: MessageListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [atBottom, setAtBottom] = useState(true);

  /*
   * Compensação de rolagem ao inserir histórico ACIMA.
   *
   * `scrollHeight` é guardado ANTES do render que insere as mensagens antigas, e
   * a diferença é somada a `scrollTop` DEPOIS — em `useLayoutEffect`, antes de o
   * navegador pintar. Fazer isso em `useEffect` produziria um salto visível de
   * um quadro.
   */
  const pendingRestore = useRef<number | null>(null);
  const previousFirstId = useRef<string | null>(null);

  const firstId = messages[0]?.id ?? null;

  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    // Histórico chegou: a primeira mensagem mudou e o total cresceu.
    if (
      pendingRestore.current !== null &&
      firstId !== previousFirstId.current &&
      firstId !== null
    ) {
      const delta = element.scrollHeight - pendingRestore.current;
      element.scrollTop += delta;
      pendingRestore.current = null;
    }
    previousFirstId.current = firstId;
  }, [firstId, messages.length]);

  const lastId = messages[messages.length - 1]?.id ?? null;

  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    /*
     * Rola para o fim SÓ se já estava no fim.
     *
     * A alternativa — rolar sempre — arranca a pessoa do trecho que está lendo
     * a cada mensagem nova, que num canal ativo é o tempo todo.
     */
    if (atBottom) {
      element.scrollTop = element.scrollHeight;
    }
  }, [lastId, atBottom]);

  const onScroll = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;

    const distanceFromBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight;
    setAtBottom(distanceFromBottom < AT_BOTTOM_THRESHOLD);

    /*
     * Carrega mais quando falta menos de uma tela para o topo.
     *
     * Disparar no topo exato faria a pessoa ver um vazio enquanto a requisição
     * corre. Uma tela de antecedência costuma esconder a latência inteira.
     */
    if (
      element.scrollTop < element.clientHeight &&
      hasMore &&
      !loadingOlder &&
      onLoadOlder
    ) {
      pendingRestore.current = element.scrollHeight;
      onLoadOlder();
    }
  }, [hasMore, loadingOlder, onLoadOlder]);

  /** Monta a sequência de itens com divisores de dia e agrupamento. */
  const items = useMemo(() => {
    const result: Array<
      | { kind: 'day'; key: string; label: string }
      | { kind: 'message'; key: string; message: ChatMessage; grouped: boolean }
    > = [];

    let previous: ChatMessage | null = null;

    for (const message of messages) {
      const sameDay =
        previous !== null &&
        new Date(previous.createdAt).toDateString() ===
          new Date(message.createdAt).toDateString();

      if (!sameDay) {
        result.push({
          kind: 'day',
          key: `day-${message.id}`,
          label: formatDayDivider(message.createdAt),
        });
      }

      const grouped =
        sameDay &&
        previous !== null &&
        previous.author?.id === message.author?.id &&
        previous.author !== null &&
        new Date(message.createdAt).getTime() -
          new Date(previous.createdAt).getTime() <
          GROUP_WINDOW_MS;

      result.push({ kind: 'message', key: message.id, message, grouped });
      previous = message;
    }

    return result;
  }, [messages]);

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      {pins !== undefined && pins.length > 0 && (
        <PinnedStrip
          pins={pins}
          {...(onJumpToMessage ? { onJump: onJumpToMessage } : {})}
          {...(onTogglePin ? { onUnpin: (id: string) => onTogglePin(id, false) } : {})}
        />
      )}

      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="scroll-hover-only min-h-0 flex-1 overflow-y-auto px-4 py-3"
      >
        {/* --------------------------------------------------------------------
            Topo: carregando, ou o marco de início da conversa
        -------------------------------------------------------------------- */}
        {loadingOlder && (
          <p
            className="py-3 text-center"
            style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}
          >
            Carregando mensagens anteriores…
          </p>
        )}

        {!hasMore && (
          <div className="px-1 pb-4 pt-6">
            <h2
              className="font-bold"
              style={{
                fontSize: 'var(--text-xl)',
                color: 'var(--color-text-primary)',
                letterSpacing: 'var(--tracking-tight)',
              }}
            >
              {direct ? channelName : `#${channelName}`}
            </h2>
            <p
              className="mt-1"
              style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)' }}
            >
              {channelTopic ??
                (direct
                  ? `Este é o começo da sua conversa com ${channelName}.`
                  : 'Este é o começo da conversa neste canal.')}
            </p>
          </div>
        )}

        {messages.length === 0 && !loadingOlder && emptyHint}

        {items.map((item) =>
          item.kind === 'day' ? (
            <DayDivider key={item.key} label={item.label} />
          ) : direct ? (
            <BubbleRow
              key={item.key}
              message={item.message}
              isOwn={item.message.author?.id === currentUserId}
              {...(onAuthorClick ? { onAuthorClick } : {})}
              {...(onRetry ? { onRetry } : {})}
            />
          ) : (
            <MessageRow
              key={item.key}
              message={item.message}
              grouped={item.grouped}
              isOwn={item.message.author?.id === currentUserId}
              mentioned={mentionsMe(item.message, currentUserId, currentUserRoleIds)}
              onRetry={onRetry}
              onAuthorClick={onAuthorClick}
              onTogglePin={onTogglePin}
            />
          ),
        )}
      </div>

      {/* ----------------------------------------------------------------------
          Botão de voltar ao fim
          ----------------------------------------------------------------------
          Aparece só quando a pessoa saiu do fim. Sem ele, quem rolou muito para
          cima num canal ativo tem que arrastar de volta manualmente.
      ---------------------------------------------------------------------- */}
      {!atBottom && (
        <button
          type="button"
          onClick={() => {
            const element = scrollRef.current;
            if (element) element.scrollTop = element.scrollHeight;
            setAtBottom(true);
          }}
          className="absolute right-4 flex items-center gap-1.5 px-3 py-1.5 transition-all"
          style={{
            bottom: 12,
            borderRadius: 'var(--radius-full)',
            background: 'var(--color-surface-overlay)',
            boxShadow:
              'var(--glass-edge), 0 6px 20px oklch(0% 0 0 / 0.40), var(--glow-soft)',
            fontSize: 'var(--text-2xs)',
            color: 'var(--color-text-secondary)',
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M2.5 4.5L6 8l3.5-3.5" stroke="currentColor" strokeWidth="1.6"
                  strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Ir para o fim
        </button>
      )}

      {/* Indicador de digitando. Altura reservada para não empurrar a lista. */}
      <div
        className="shrink-0 px-4"
        style={{ height: 18, fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}
        aria-live="polite"
      >
        {typing.length > 0 && (
          <span>
            {typing.length === 1
              ? `${typing[0]} está digitando…`
              : typing.length === 2
                ? `${typing[0]} e ${typing[1]} estão digitando…`
                : `${typing.length} pessoas estão digitando…`}
          </span>
        )}
      </div>
    </div>
  );
}

/* ==============================================================================
   Divisor de dia
============================================================================== */

function DayDivider({ label }: { label: string }) {
  return (
    <div className="my-3 flex items-center gap-3" aria-hidden="true">
      <span className="h-px flex-1" style={{ background: 'var(--color-border-subtle)' }} />
      <span
        className="shrink-0 font-semibold uppercase"
        style={{
          fontSize: 'var(--text-2xs)',
          letterSpacing: 'var(--tracking-wide)',
          color: 'var(--color-text-tertiary)',
        }}
      >
        {label}
      </span>
      <span className="h-px flex-1" style={{ background: 'var(--color-border-subtle)' }} />
    </div>
  );
}

/* ==============================================================================
   Uma mensagem
============================================================================== */

function MessageRow({
  message,
  grouped,
  isOwn,
  mentioned,
  onRetry,
  onAuthorClick,
  onTogglePin,
}: {
  message: ChatMessage;
  grouped: boolean;
  isOwn: boolean;
  /** A mensagem me menciona: fundo de acento e barra na borda esquerda. */
  mentioned: boolean;
  onRetry?: ((messageId: string) => void) | undefined;
  onAuthorClick?: ((userId: string) => void) | undefined;
  onTogglePin?: ((messageId: string, pinned: boolean) => void) | undefined;
}) {
  const [hover, setHover] = useState(false);
  const author = message.author;
  const pinned = message.pinned === true;

  return (
    <div
      id={`msg-${message.id}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="group relative flex gap-3 px-1"
      style={{
        paddingTop: grouped ? 1 : 8,
        paddingBottom: 1,
        /* A mensagem mencionada recebe recuo à esquerda para a barra de acento
           caber sem cobrir o avatar. */
        paddingLeft: mentioned ? 10 : 4,
        borderRadius: 'var(--radius-sm)',
        background: mentioned
          ? 'var(--color-accent-subtle)'
          : hover
            ? 'var(--color-surface-hover)'
            : 'transparent',
        /*
         * Pendente fica atenuada, falha fica marcada.
         *
         * A distinção importa: pendente é normal e se resolve sozinha; falha
         * exige ação. Tratar as duas igual faria toda mensagem parecer com
         * problema por um instante.
         */
        opacity: message.pending ? 0.6 : 1,
      }}
    >
      {/* Barra vertical de acento na borda esquerda. Dois canais para a mesma
          informação — o fundo tingido e a barra — porque o fundo sozinho, a 16%
          de alfa, desaparece para quem tem baixa sensibilidade a contraste. */}
      {mentioned && (
        <span
          aria-hidden="true"
          className="absolute inset-y-0 left-0 rounded-full"
          style={{
            width: 3,
            background: 'var(--color-accent)',
            boxShadow: '0 0 10px var(--color-accent-glow)',
          }}
        />
      )}
      {/* Coluna do avatar. Em mensagem agrupada, mostra a hora no hover. */}
      <div className="w-10 shrink-0">
        {!grouped && author && (
          <Avatar
            media={author.avatar ?? null}
            name={displayNameOf(author)}
            seed={author.id}
            size={40}
            {...(onAuthorClick ? { onClick: () => onAuthorClick(author.id) } : {})}
          />
        )}
        {grouped && hover && (
          <span
            className="block pt-0.5 text-right"
            style={{
              fontSize: 9,
              fontFamily: 'var(--font-mono)',
              color: 'var(--color-text-tertiary)',
            }}
          >
            {formatTime(message.createdAt)}
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        {!grouped && (
          <div className="flex items-baseline gap-2">
            <button
              type="button"
              onClick={author && onAuthorClick ? () => onAuthorClick(author.id) : undefined}
              className="font-semibold"
              style={{
                fontSize: 'var(--text-sm)',
                color: author?.color ?? 'var(--color-text-primary)',
                cursor: onAuthorClick ? 'pointer' : 'default',
              }}
            >
              {author ? displayNameOf(author) : 'Usuário removido'}
            </button>

            {author?.isBot && (
              <span
                className="font-bold uppercase"
                style={{
                  fontSize: 9,
                  padding: '1px 4px',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--color-accent-subtle)',
                  color: 'var(--color-accent-on-subtle)',
                  letterSpacing: 'var(--tracking-wide)',
                }}
              >
                bot
              </span>
            )}

            {/*
              Na linha mencionada o metadado SOBE de nível de texto.
              -----------------------------------------------------
              Medido: terciário sobre `--color-accent-subtle` composto em
              `--color-surface-raised` dá entre 4.16:1 e 4.42:1 conforme o
              acento escolhido — reprova AA em quatro das cinco paletas. O fundo
              tingido clareia a superfície, e o token calibrado para a superfície
              nua não sobrevive a isso.

              É a mesma regra que theme.css já documenta para hover: linha com
              fundo próprio sobe de nível de texto em vez de manter o mesmo.
            */}
            <span
              style={{
                fontSize: 'var(--text-2xs)',
                color: mentioned
                  ? 'var(--color-text-secondary)'
                  : 'var(--color-text-tertiary)',
              }}
            >
              {formatTime(message.createdAt)}
            </span>

            {/* Alfinete DISCRETO e não uma pastilha "FIXADA": a faixa no topo já
                lista as fixadas, e o marcador aqui só confirma que esta é uma
                delas. Uma pastilha por mensagem fixada encheria a conversa. */}
            {pinned && (
              <span
                title="Mensagem fixada neste canal"
                aria-label="Mensagem fixada"
                style={{ color: 'var(--color-accent-on-subtle)' }}
              >
                <PinIcon filled />
              </span>
            )}
          </div>
        )}

        {message.content !== null && (
          <p
            className="whitespace-pre-wrap break-words"
            style={{
              fontSize: 'var(--text-sm)',
              lineHeight: 'var(--leading-normal)',
              /* Mensagem que me menciona sobe de nível de texto: o fundo de
                 acento clareia a superfície, e o secundário perderia contraste
                 exatamente na mensagem que eu mais preciso ler. */
              color: mentioned
                ? 'var(--color-text-primary)'
                : 'var(--color-text-secondary)',
            }}
          >
            {renderContent(message.content, mentioned)}
            {message.editedAt !== null && (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 'var(--text-2xs)',
                  /* Mesmo motivo do timestamp acima: o fundo tingido derruba o
                     terciário abaixo de AA. */
                  color: mentioned
                    ? 'var(--color-text-secondary)'
                    : 'var(--color-text-tertiary)',
                }}
                title={`Editada em ${new Date(message.editedAt).toLocaleString('pt-BR')}`}
              >
                (editada)
              </span>
            )}
          </p>
        )}

        {message.attachments !== undefined && message.attachments.length > 0 && (
          <div className="mt-1.5 flex flex-col gap-1.5">
            {message.attachments.map((attachment) => (
              <AttachmentPreview key={attachment.id} attachment={attachment} />
            ))}
          </div>
        )}

        {message.failed && (
          <div className="mt-1 flex items-center gap-2">
            <span
              style={{
                fontSize: 'var(--text-2xs)',
                color: 'var(--color-danger-on-subtle)',
              }}
            >
              Não foi enviada.
            </span>
            {onRetry && (
              <button
                type="button"
                onClick={() => onRetry(message.id)}
                className="underline"
                style={{
                  fontSize: 'var(--text-2xs)',
                  color: 'var(--color-accent-on-subtle)',
                }}
              >
                Tentar de novo
              </button>
            )}
          </div>
        )}
      </div>

      {isOwn && message.pending && (
        <span
          className="shrink-0 self-start pt-1"
          style={{ fontSize: 9, color: 'var(--color-text-tertiary)' }}
          title="Enviando"
          aria-label="Enviando"
        >
          ⋯
        </span>
      )}

      {/* ----------------------------------------------------------------------
          Ações da mensagem — cápsula flutuante no canto superior direito
          ----------------------------------------------------------------------
          Sobreposta e não em coluna própria: uma coluna reservada para ações
          roubaria largura de TODAS as mensagens para servir a que está sob o
          cursor. A cápsula fica sobre a borda de cima, onde raramente há texto,
          e tem vidro para o que estiver embaixo continuar legível.

          Aparece no hover E no foco dentro da mensagem — sem o segundo caso, a
          ação de fixar seria inalcançável por teclado.
      ---------------------------------------------------------------------- */}
      {onTogglePin !== undefined && !message.pending && (
        <div
          /*
           * A visibilidade fica em CLASSE e não em estilo inline, e a diferença
           * é funcional: estilo inline ganha de qualquer classe, então um
           * `opacity` inline anularia o `focus-within` e a ação só existiria
           * para quem usa mouse.
           *
           * `pointer-events-none` no estado invisível é obrigatório — um
           * elemento a opacidade zero continua engolindo clique, e ele fica
           * exatamente sobre a borda de cima da mensagem.
           */
          className="glass absolute opacity-0 transition-opacity pointer-events-none focus-within:pointer-events-auto focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100"
          style={{
            top: -6,
            right: 6,
            padding: 2,
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--glass-edge), 0 4px 14px oklch(0% 0 0 / 0.45)',
            transitionDuration: 'var(--duration-fast)',
          }}
        >
          <button
            type="button"
            onClick={() => onTogglePin(message.id, !pinned)}
            aria-label={pinned ? 'Desfixar mensagem' : 'Fixar mensagem no canal'}
            title={pinned ? 'Desfixar mensagem' : 'Fixar mensagem no canal'}
            aria-pressed={pinned}
            className="grid place-items-center"
            style={{
              width: 26,
              height: 26,
              borderRadius: 'var(--radius-sm)',
              color: pinned
                ? 'var(--color-accent-on-subtle)'
                : 'var(--color-text-tertiary)',
              background: pinned ? 'var(--color-accent-subtle)' : 'transparent',
            }}
          >
            <PinIcon filled={pinned} />
          </button>
        </div>
      )}
    </div>
  );
}

/* ==============================================================================
   Bolha — conversa entre duas pessoas
   ==============================================================================
   O formato existe SÓ na conversa direta, e a razão é sobre quantos autores há.

   Numa conversa de dois, "de quem é" tem duas respostas possíveis, e o LADO
   responde as duas antes de o olho chegar ao nome. Num canal com dez pessoas o
   lado não identifica ninguém — sobrariam dois lados para dez autores — e o que
   funciona lá é o que já está em `MessageRow`: tudo alinhado à esquerda, uma
   coluna de avatares que se varre de cima a baixo.

   Por isso as duas formas coexistem em vez de uma virar a outra. Não é
   preferência estética: é a mesma informação servida por um recurso visual que
   só tem capacidade para dois valores.

   `Nome, HH:MM` fica DENTRO da bolha e acima do texto. Fora dela, o rótulo
   flutuaria entre duas bolhas e a associação viraria uma questão de proximidade
   — e proximidade é ambígua quando as mensagens se alternam de lado. Dentro, o
   rótulo pertence a um recipiente e não há o que interpretar.

   Sem agrupamento por autor, ao contrário do canal: cada bolha carrega o próprio
   rótulo. Numa sequência de três mensagens da mesma pessoa em dois minutos, o
   agrupamento economizaria dois rótulos e tiraria a hora de duas mensagens — e
   numa conversa direta a hora de cada mensagem é o que se consulta para saber
   quanto tempo alguém levou para responder.
============================================================================== */

function BubbleRow({
  message,
  isOwn,
  onAuthorClick,
  onRetry,
}: {
  message: ChatMessage;
  isOwn: boolean;
  onAuthorClick?: ((userId: string) => void) | undefined;
  onRetry?: ((messageId: string) => void) | undefined;
}) {
  const author = message.author;

  /*
   * "Você" para as próprias mensagens, e não o próprio nome.
   *
   * O nome próprio repetido a cada bolha do lado direito é informação que a
   * pessoa já tem — ela sabe o que escreveu. "Você" ocupa o mesmo lugar
   * gramatical e some da leitura, que é o que se quer de um rótulo redundante.
   */
  const rotulo = isOwn
    ? 'Você'
    : author
      ? displayNameOf(author)
      : 'Usuário removido';

  return (
    <div
      id={`msg-${message.id}`}
      className="flex gap-2.5 px-1"
      style={{
        paddingTop: 6,
        paddingBottom: 6,
        justifyContent: isOwn ? 'flex-end' : 'flex-start',
        opacity: message.pending ? 0.6 : 1,
      }}
    >
      {/*
        O avatar aparece SÓ nas mensagens recebidas.

        Do lado direito ele seria o mesmo avatar repetido em toda bolha da
        pessoa que está usando o app — e a única pergunta que um avatar responde
        ("quem?") já está respondida pelo lado.
      */}
      {!isOwn && (
        <div className="shrink-0 self-end pb-0.5">
          <Avatar
            media={author?.avatar ?? null}
            name={rotulo}
            seed={author?.id ?? message.id}
            size={32}
            {...(author && onAuthorClick
              ? { onClick: () => onAuthorClick(author.id) }
              : {})}
          />
        </div>
      )}

      <div
        className="min-w-0"
        style={{
          /*
           * 74% e não 100%: uma bolha que atravessa a largura toda perde o lado,
           * e o lado é o que diz de quem ela é. O limite garante que sempre
           * sobre vazio do outro lado para a assimetria ser visível.
           */
          maxWidth: '74%',
          padding: '7px 12px 8px',
          borderRadius: 'var(--radius-lg)',
          /*
           * O canto colado no avatar (ou na borda, quando é a própria) fica
           * MENOS arredondado. É o que ancora a bolha a um lado — com os quatro
           * cantos iguais ela flutua, e a leitura de "veio daquele lado" some.
           */
          borderBottomLeftRadius: isOwn ? 'var(--radius-lg)' : 'var(--radius-xs)',
          borderBottomRightRadius: isOwn ? 'var(--radius-xs)' : 'var(--radius-lg)',
          background: isOwn
            ? 'var(--color-accent-subtle)'
            : 'var(--color-surface-raised)',
          boxShadow: isOwn
            ? 'none'
            : '0 0 0 1px var(--color-border-subtle)',
        }}
      >
        <div
          className="flex items-baseline gap-1.5"
          style={{
            fontSize: 'var(--text-2xs)',
            /*
             * A própria bolha tem fundo TINGIDO, e sobre ele o terciário reprova
             * AA — é a mesma medição registrada em `MessageRow` para a mensagem
             * mencionada. Fundo com cor sobe o rótulo um nível de texto.
             */
            color: isOwn
              ? 'var(--color-text-secondary)'
              : 'var(--color-text-tertiary)',
            justifyContent: isOwn ? 'flex-end' : 'flex-start',
          }}
        >
          <span className="truncate font-medium">{rotulo},</span>
          <span className="shrink-0" style={{ fontFamily: 'var(--font-mono)' }}>
            {formatTime(message.createdAt)}
          </span>
        </div>

        {message.content !== null && (
          <p
            className="whitespace-pre-wrap break-words"
            style={{
              marginTop: 2,
              fontSize: 'var(--text-sm)',
              lineHeight: 'var(--leading-normal)',
              color: 'var(--color-text-primary)',
              textAlign: isOwn ? 'right' : 'left',
            }}
          >
            {renderContent(message.content, isOwn)}
            {message.editedAt !== null && (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 'var(--text-2xs)',
                  color: 'var(--color-text-secondary)',
                }}
                title={`Editada em ${new Date(message.editedAt).toLocaleString('pt-BR')}`}
              >
                (editada)
              </span>
            )}
          </p>
        )}

        {message.attachments !== undefined && message.attachments.length > 0 && (
          <div className="mt-1.5 flex flex-col gap-1.5">
            {message.attachments.map((attachment) => (
              <AttachmentPreview key={attachment.id} attachment={attachment} />
            ))}
          </div>
        )}

        {message.failed && (
          <div
            className="mt-1 flex items-center gap-2"
            style={{ justifyContent: isOwn ? 'flex-end' : 'flex-start' }}
          >
            <span
              style={{
                fontSize: 'var(--text-2xs)',
                color: 'var(--color-danger-on-subtle)',
              }}
            >
              Não foi enviada.
            </span>
            {onRetry && (
              <button
                type="button"
                onClick={() => onRetry(message.id)}
                className="underline"
                style={{
                  fontSize: 'var(--text-2xs)',
                  color: 'var(--color-accent-on-subtle)',
                }}
              >
                Tentar de novo
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ==============================================================================
   Faixa de mensagens fixadas
   ==============================================================================
   Recolhida por padrão, mostrando só a contagem.

   Expandida por padrão seria pior: num canal com oito fixadas, a faixa comeria
   um terço da altura da conversa a cada abertura, e a informação que ela carrega
   é de consulta — não é o que a pessoa vem ver. Recolhida ela cabe em 30px e
   ainda responde "existe algo fixado aqui?", que é a pergunta frequente.
============================================================================== */

function PinnedStrip({
  pins,
  onJump,
  onUnpin,
}: {
  pins: PinnedMessage[];
  onJump?: ((messageId: string) => void) | undefined;
  onUnpin?: ((messageId: string) => void) | undefined;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="shrink-0"
      style={{
        /* Véu de acento e aresta de luz embaixo. A faixa pertence ao canal, não
           à conversa — o tingido a separa da lista sem uma linha divisória. */
        background: 'var(--color-accent-subtle)',
        boxShadow: 'inset 0 -1px 0 oklch(100% 0 0 / 0.08)',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center px-4 py-1.5 text-left"
        style={{ gap: 'var(--spacing-2)' }}
      >
        <span aria-hidden="true" style={{ color: 'var(--color-accent-on-subtle)' }}>
          <PinIcon filled />
        </span>
        <span className="min-w-0 flex-1 truncate font-medium"
              style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-primary)' }}>
          {pins.length === 1
            ? '1 mensagem fixada'
            : `${pins.length} mensagens fixadas`}
        </span>
        <span aria-hidden="true"
              className="shrink-0 transition-transform"
              style={{
                color: 'var(--color-text-tertiary)',
                transform: open ? 'rotate(180deg)' : 'none',
                transitionDuration: 'var(--duration-fast)',
              }}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2.5 4.5L6 8l3.5-3.5" stroke="currentColor" strokeWidth="1.6"
                  strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>

      {open && (
        <ul className="flex flex-col px-2 pb-2" style={{ maxHeight: 200, overflowY: 'auto' }}>
          {pins.map((pin) => (
            <li key={pin.messageId} className="flex items-center" style={{ gap: 2 }}>
              <button
                type="button"
                onClick={() => onJump?.(pin.messageId)}
                disabled={!onJump}
                className="min-w-0 flex-1 px-2 py-1.5 text-left"
                style={{
                  borderRadius: 'var(--radius-sm)',
                  cursor: onJump ? 'pointer' : 'default',
                }}
              >
                <span className="block truncate font-medium"
                      style={{
                        fontSize: 'var(--text-2xs)',
                        color: 'var(--color-accent-on-subtle)',
                      }}>
                  {pin.authorName}
                </span>
                <span className="block truncate"
                      style={{
                        fontSize: 'var(--text-xs)',
                        color: 'var(--color-text-secondary)',
                      }}>
                  {pin.content ?? 'Mensagem sem texto'}
                </span>
              </button>

              {onUnpin && (
                <button
                  type="button"
                  onClick={() => onUnpin(pin.messageId)}
                  aria-label={`Desfixar mensagem de ${pin.authorName}`}
                  title="Desfixar"
                  className="grid shrink-0 place-items-center"
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 'var(--radius-sm)',
                    color: 'var(--color-text-tertiary)',
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
                       aria-hidden="true">
                    <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor"
                          strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PinIcon({ filled = false }: { filled?: boolean }) {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M9.4 1.8l4.8 4.8-1.5 1.5-1-.3-3 3 .4 3.3-1.2 1.2-2.5-3.6-3.1 2.1 2.1-3.1L.8 8.2 2 7l3.3.4 3-3-.3-1 1.4-1.6z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
        {...(filled ? { fill: 'currentColor', fillOpacity: 0.28 } : {})}
      />
    </svg>
  );
}

/* ==============================================================================
   Anexo
   ==============================================================================
   Vídeo e áudio ganham reprodutor inline; o resto vira link com tamanho.

   `preload="metadata"` e não `auto`: `auto` baixaria o vídeo inteiro de cada
   anexo visível na rolagem, o que num canal com muitos vídeos consome banda
   sem ninguém ter pedido. `metadata` traz só duração e dimensão, que é o que a
   interface precisa para reservar o espaço.
============================================================================== */

function AttachmentPreview({ attachment }: { attachment: MessageAttachment }) {
  const [revealed, setRevealed] = useState(!attachment.spoiler);
  const isVideo = attachment.mimeType.startsWith('video/');
  const isAudio = attachment.mimeType.startsWith('audio/');
  const isImage = attachment.mimeType.startsWith('image/');

  if (!revealed) {
    return (
      <button
        type="button"
        onClick={() => setRevealed(true)}
        className="flex items-center gap-2 px-3 py-2 text-left"
        style={{
          borderRadius: 'var(--radius-md)',
          background: 'var(--color-surface-sunken)',
          boxShadow: '0 0 0 1px var(--color-border-default)',
          fontSize: 'var(--text-xs)',
          color: 'var(--color-text-tertiary)',
          maxWidth: 420,
        }}
      >
        Conteúdo marcado como spoiler — clique para mostrar
      </button>
    );
  }

  if (isVideo) {
    return (
      <video
        src={attachment.url}
        controls
        preload="metadata"
        /* `playsInline` evita que o iOS abra em tela cheia ao dar play, o que
           tiraria a pessoa da conversa. */
        playsInline
        style={{
          maxWidth: 480,
          maxHeight: 320,
          borderRadius: 'var(--radius-md)',
          background: 'var(--color-surface-sunken)',
        }}
      >
        <track kind="captions" />
      </video>
    );
  }

  if (isAudio) {
    return (
      <div
        className="flex items-center gap-3 px-3 py-2"
        style={{
          borderRadius: 'var(--radius-md)',
          background: 'var(--color-surface-sunken)',
          maxWidth: 420,
        }}
      >
        <audio src={attachment.url} controls preload="metadata" style={{ height: 32 }}>
          <track kind="captions" />
        </audio>
        <span
          className="truncate"
          style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}
        >
          {attachment.filename}
        </span>
      </div>
    );
  }

  if (isImage) {
    return (
      <img
        src={attachment.url}
        alt={attachment.filename}
        loading="lazy"
        decoding="async"
        /* Dimensões declaradas quando conhecidas: sem elas, a imagem empurra a
           lista ao carregar e a rolagem salta. */
        {...(attachment.width != null ? { width: attachment.width } : {})}
        {...(attachment.height != null ? { height: attachment.height } : {})}
        style={{
          maxWidth: 420,
          maxHeight: 320,
          width: 'auto',
          height: 'auto',
          borderRadius: 'var(--radius-md)',
        }}
      />
    );
  }

  return (
    <a
      href={attachment.url}
      download={attachment.filename}
      className="flex items-center gap-2 px-3 py-2"
      style={{
        borderRadius: 'var(--radius-md)',
        background: 'var(--color-surface-sunken)',
        boxShadow: '0 0 0 1px var(--color-border-subtle)',
        fontSize: 'var(--text-xs)',
        color: 'var(--color-text-secondary)',
        maxWidth: 420,
      }}
    >
      <span className="truncate">{attachment.filename}</span>
      <span
        className="shrink-0"
        style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}
      >
        {formatBytes(attachment.byteSize)}
      </span>
    </a>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/* ==============================================================================
   Composição
============================================================================== */

export interface ComposerProps {
  channelName: string;
  /**
   * Conversa direta: o campo diz "Conversar com Luna", não "Conversar em #Luna".
   *
   * O `#` é a sintaxe de canal. Numa conversa com uma pessoa ele afirma algo
   * falso sobre o destino, e o placeholder é justamente onde a pessoa confere
   * para onde a mensagem vai antes de apertar Enter.
   */
  direct?: boolean;
  /**
   * Abre o seletor de emoji. Ausente = o botão não aparece.
   *
   * Ausente e não desabilitado: um rosto cinza permanente ao lado do campo
   * ensina a ignorar aquela área, e é a mesma área onde vive o anexo.
   */
  onOpenEmoji?: ((x: number, y: number) => void) | undefined;
  disabled?: boolean;
  /** Motivo do bloqueio, quando `disabled`. */
  disabledReason?: string;
  /** Slowmode em segundos. 0 = desligado. */
  rateLimitSeconds?: number;
  onSend: (content: string) => void | Promise<void>;
  onTyping?: () => void;
  onAttach?: (files: File[]) => void;
  /** `false` quando o servidor não tem armazenamento configurado. */
  attachmentsEnabled?: boolean;
  attachmentsDisabledReason?: string;
}

export function Composer({
  channelName,
  direct = false,
  onOpenEmoji,
  disabled = false,
  disabledReason,
  rateLimitSeconds = 0,
  onSend,
  onTyping,
  onAttach,
  attachmentsEnabled = true,
  attachmentsDisabledReason,
}: ComposerProps) {
  const [value, setValue] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const lastTypingSentAt = useRef(0);

  /*
   * Altura automática, com teto.
   *
   * Sem teto, colar um texto de 200 linhas cobre a conversa inteira. 160px são
   * cerca de 7 linhas — suficiente para revisar o que se escreveu sem engolir a
   * tela.
   */
  useEffect(() => {
    const element = textareaRef.current;
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${Math.min(160, element.scrollHeight)}px`;
  }, [value]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const send = useCallback(async () => {
    const content = value.trim();
    if (content.length === 0 || disabled || cooldown > 0) return;

    // Limpa ANTES de enviar: a UI otimista mostra a mensagem na hora, e deixar o
    // texto no campo até a resposta faria parecer que não foi enviado.
    setValue('');
    if (rateLimitSeconds > 0) setCooldown(rateLimitSeconds);

    await onSend(content);
  }, [value, disabled, cooldown, rateLimitSeconds, onSend]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      /*
       * Enter envia; Shift+Enter quebra linha.
       *
       * `isComposing` é a checagem que falta na maioria das implementações: com
       * teclado de composição (acentos em alguns layouts, IME de japonês), Enter
       * CONFIRMA a composição. Enviar aí cortaria a palavra no meio.
       */
      if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
        event.preventDefault();
        void send();
      }
    },
    [send],
  );

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setValue(event.target.value);

      /*
       * Aviso de digitando com throttle de 3 segundos.
       *
       * Um evento por tecla inundaria o gateway — e o indicador só precisa
       * aparecer, não ser preciso ao milissegundo. O servidor expira o
       * indicador sozinho, então parar de enviar é suficiente para ele
       * desaparecer.
       */
      const now = Date.now();
      if (onTyping && now - lastTypingSentAt.current > 3000) {
        lastTypingSentAt.current = now;
        onTyping();
      }
    },
    [onTyping],
  );

  if (disabled) {
    return (
      <div className="shrink-0 px-4 pb-3">
        <div
          className="px-3 py-2.5"
          style={{
            borderRadius: 'var(--radius-lg)',
            background: 'var(--color-surface-sunken)',
            boxShadow: '0 0 0 1px var(--color-border-subtle)',
            fontSize: 'var(--text-xs)',
            color: 'var(--color-text-tertiary)',
          }}
        >
          {disabledReason ?? 'Você não pode enviar mensagens neste canal.'}
        </div>
      </div>
    );
  }

  return (
    <div className="shrink-0 px-4 pb-3">
      <div
        className="flex items-end gap-2 px-2 py-1.5"
        style={{
          borderRadius: 'var(--radius-lg)',
          background: 'var(--color-surface-sunken)',
          boxShadow: 'inset 0 1px 0 oklch(0% 0 0 / 0.24), 0 0 0 1px var(--color-border-default)',
        }}
      >
        {onAttach && (
          <label
            className="grid shrink-0 place-items-center"
            title={
              attachmentsEnabled
                ? 'Anexar arquivo'
                : (attachmentsDisabledReason ??
                    'Anexos indisponíveis: armazenamento não configurado.')
            }
            style={{
              width: 30,
              height: 30,
              borderRadius: 'var(--radius-md)',
              color: attachmentsEnabled
                ? 'var(--color-text-tertiary)'
                : 'var(--color-text-disabled)',
              cursor: attachmentsEnabled ? 'pointer' : 'not-allowed',
            }}
          >
            <input
              type="file"
              multiple
              className="sr-only"
              disabled={!attachmentsEnabled}
              accept="image/*,video/*,audio/*"
              onChange={(event) => {
                const files = [...(event.target.files ?? [])];
                if (files.length > 0) onAttach(files);
                // Limpa para permitir escolher o MESMO arquivo de novo: sem
                // isto, o `change` não dispara na segunda vez.
                event.target.value = '';
              }}
            />
            {/*
              Clipe de papel, e não o "+" de antes.

              O "+" é o glifo de CRIAR, e no Discord ele abre um menu com várias
              coisas. Aqui a ação é uma só — anexar um arquivo — e o clipe diz
              qual é sem precisar de um menu para explicar.
            */}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"
                 stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
                 strokeLinejoin="round">
              <path d="M9.9 4.2L5.1 9a2.05 2.05 0 002.9 2.9l5.1-5.1a3.4 3.4 0 00-4.8-4.8L3 7.3a4.75 4.75 0 006.7 6.7l3-3" />
            </svg>
            <span className="sr-only">Anexar arquivo</span>
          </label>
        )}

        {/* --------------------------------------------------------------------
            Emoji
            --------------------------------------------------------------------
            Ao lado do anexo e não no fim, junto do enviar: os dois abrem algo
            PARA compor a mensagem, e o enviar a despacha. Agrupar por
            consequência mantém o lado direito com uma função só — e o botão de
            enviar não ganha vizinho com quem ser confundido no clique rápido.
        -------------------------------------------------------------------- */}
        {onOpenEmoji && (
          <button
            type="button"
            onClick={(event) => {
              const r = event.currentTarget.getBoundingClientRect();
              onOpenEmoji(r.left, r.top);
            }}
            title="Emoji"
            aria-label="Inserir emoji"
            className="grid shrink-0 place-items-center transition-colors"
            style={{
              width: 30,
              height: 30,
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-text-tertiary)',
            }}
            onMouseEnter={(event) => {
              event.currentTarget.style.color = 'var(--color-text-primary)';
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.color = 'var(--color-text-tertiary)';
            }}
          >
            <svg width="17" height="17" viewBox="0 0 16 16" fill="none" aria-hidden="true"
                 stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
              <circle cx="8" cy="8" r="6.1" />
              <path d="M5.6 9.6a3 3 0 004.8 0" />
              <path d="M6 6.2v.01M10 6.2v.01" strokeWidth="1.8" />
            </svg>
          </button>
        )}

        <textarea
          ref={textareaRef}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          rows={1}
          maxLength={8000}
          placeholder={
            cooldown > 0
              ? `Aguarde ${cooldown}s (modo lento)`
              : direct
                ? `Conversar com ${channelName}`
                : `Conversar em #${channelName}`
          }
          aria-label={`Mensagem para ${channelName}`}
          className="min-w-0 flex-1 resize-none bg-transparent py-1.5 outline-none"
          style={{
            fontSize: 'var(--text-sm)',
            lineHeight: 'var(--leading-normal)',
            color: 'var(--color-text-primary)',
            maxHeight: 160,
          }}
        />

        <button
          type="button"
          onClick={() => void send()}
          disabled={value.trim().length === 0 || cooldown > 0}
          aria-label="Enviar"
          className="grid shrink-0 place-items-center transition-all disabled:opacity-40"
          style={{
            width: 30,
            height: 30,
            borderRadius: 'var(--radius-md)',
            background:
              value.trim().length > 0 && cooldown === 0
                ? 'var(--color-accent-solid)'
                : 'transparent',
            color:
              value.trim().length > 0 && cooldown === 0
                ? 'var(--color-on-solid)'
                : 'var(--color-text-tertiary)',
          }}
        >
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M2.5 8h11M9 3.5L13.5 8 9 12.5" stroke="currentColor"
                  strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {value.length > 7500 && (
        <p
          className="pt-1 text-right"
          style={{
            fontSize: 'var(--text-2xs)',
            color:
              value.length >= 8000
                ? 'var(--color-danger-on-subtle)'
                : 'var(--color-text-tertiary)',
          }}
        >
          {value.length} / 8000
        </p>
      )}
    </div>
  );
}
